import { useSessionStorage, type RemovableRef } from '@vueuse/core';
import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';

import type { AllowableProductModel } from '@/models/allowable-product-model.model';
import type { MatrixProduct } from '@/models/matrix-product.model';
import type { MatrixOrder } from '@/types/matrix.interface';
import type { MatrixOrderProduct } from '@/types/product.interface';
import moment from 'moment';

interface State {
  products: Record<string, MatrixProduct>;
  activeMatrixOrderIndex: number;
  matrixUpdated: RemovableRef<boolean>;
  matrixOrders: RemovableRef<MatrixOrder[]>;
  selectedProductModelsNames: RemovableRef<Set<string>>;
  productModels: Map<string, AllowableProductModel>;
}

export const useProductMatrixStore = defineStore('product-matrix', {
  state: (): State => ({
    products: {},
    activeMatrixOrderIndex: 0,
    matrixUpdated: useSessionStorage('productMatrix/matrixUpdated', false),
    matrixOrders: useSessionStorage('productMatrix/matrixOrders', []),
    selectedProductModelsNames: useSessionStorage('productMatrix/selectedProductModelsNames', new Set<string>()),
    productModels: new Map<string, AllowableProductModel>(),
  }),

  getters: {
    activeMatrixOrder(state): MatrixOrder {
      return state.matrixOrders[state.activeMatrixOrderIndex];
    },
    selectedProducts(): MatrixOrderProduct[] {
      return this.activeMatrixOrder?.matrixProducts ? Object.values(this.activeMatrixOrder.matrixProducts) : [];
    },
  },

  actions: {
    setProducts(products: Record<string, MatrixProduct>) {
      this.products = products;
    },

    setMatrixOrders(matrixOrders: MatrixOrder[]) {
      this.matrixOrders = matrixOrders;
    },

    setMatrixOrder(matrixOrder: MatrixOrder) {
      this.matrixOrders.push(matrixOrder);
    },

    setActiveMatrixOrderIndex(index: number) {
      if (this.matrixOrders[index]) this.activeMatrixOrderIndex = index;
    },

    updateDateIfLaunchDateMoveToLater() {
      for (const matrixOrder of this.matrixOrders) {
        const latestLaunchDate = this.findLatestProductLaunchDate(matrixOrder);
        this.syncOrderDateWithLaunchDate(matrixOrder, latestLaunchDate);
      }
    },

    findLatestProductLaunchDate(matrixOrder: MatrixOrder): moment.Moment {
      let latestLaunchDate = moment(matrixOrder.date);
      for (const productId in matrixOrder.matrixProducts) {
        const product = this.products[productId];
        if (product && moment(product.launchDate).isAfter(latestLaunchDate)) {
          latestLaunchDate = moment(product.launchDate);
        }
      }

      return latestLaunchDate;
    },

    syncOrderDateWithLaunchDate(matrixOrder: MatrixOrder, latestLaunchDate: moment.Moment) {
      const orderDate = moment(matrixOrder.date);
      const isLaunchDateLater = latestLaunchDate.isAfter(orderDate, 'day');
      if (isLaunchDateLater) {
        matrixOrder.date = latestLaunchDate.toDate();
        this.setUpdateStatus(true);
      }
    },

    initMatrixOrder(date: Date = new Date()) {
      if (!this.matrixOrders.length) {
        const matrixOrder: MatrixOrder = {
          date: date,
          matrixProducts: {},
          total: 0,
        };

        this.matrixOrders.push(matrixOrder);
        this.setActiveMatrixOrderIndex(this.matrixOrders.length - 1);
      }
    },

    deleteMatrixOrder(index: number) {
      this.matrixOrders.splice(index, 1);

      if (this.activeMatrixOrderIndex >= this.matrixOrders.length) {
        this.setActiveMatrixOrderIndex(this.matrixOrders.length - 1);
      } else if (index <= this.activeMatrixOrderIndex && this.activeMatrixOrderIndex > 0) {
        this.setActiveMatrixOrderIndex(this.activeMatrixOrderIndex - 1);
      }
    },

    cloneMatrixOrder(index: number) {
      const matrixOrder = cloneDeep(this.matrixOrders[index]);

      this.matrixOrders.push({
        date: matrixOrder.date,
        matrixProducts: matrixOrder.matrixProducts,
        total: matrixOrder.total,
      });

      const newIndex = this.matrixOrders.length - 1;
      this.setActiveMatrixOrderIndex(newIndex);

      this.setUpdateStatus(true);
    },

    updateMatrixOrder(matrixOrder: Partial<MatrixOrder>, index: number = 0) {
      const current = this.matrixOrders[index];

      this.matrixOrders[index] = {
        ...current,
        ...matrixOrder,
      };

      this.setUpdateStatus(true);
    },

    setMatrixOrderTotal(total: number, index: number = 0) {
      const current = this.matrixOrders[index];

      this.matrixOrders[index] = {
        ...current,
        total,
      };
    },

    setUpdateStatus(status: boolean) {
      this.matrixUpdated = status;
    },

    setProductModels(assortments: Map<string, AllowableProductModel>) {
      this.productModels = assortments;
    },

    setSelectedProductModels(productModelsNames: Set<string>) {
      this.selectedProductModelsNames = productModelsNames;
    },

    updateSelectedProductModel(productModelNames: Set<string>) {
      productModelNames.forEach((name) => this.selectedProductModelsNames.add(name));
    },

    addSelectedProduct(allowableProduct: AllowableProductModel) {
      this.selectedProductModelsNames.add(allowableProduct.name);
    },

    removeSelectedProduct(allowableProduct: AllowableProductModel) {
      this.selectedProductModelsNames.delete(allowableProduct.name);
    },

    addAllSelectedProducts(allowableProducts: AllowableProductModel[]) {
      allowableProducts.forEach((allowableProduct) => {
        this.selectedProductModelsNames.add(allowableProduct.name);
      });
    },

    removeSelectedProducts(allowableProducts: AllowableProductModel[]) {
      allowableProducts.forEach((allowableProduct) => {
        this.selectedProductModelsNames.delete(allowableProduct.name);
      });
    },

    reset() {
      this.selectedProductModelsNames = new Set<string>();
      this.activeMatrixOrderIndex = 0;
      this.matrixOrders = [];
      this.products = {};
      this.matrixUpdated = false;
    },
  },
});
